import {
  Button,
  FormCheckbox,
  FormInput,
  FormPassword,
  FormRoot,
  Loader,
  RHF,
  toast,
  zodResolver,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import z from "zod";
import { authClient } from "../api/client";
import { useAuthState } from "../model/useAuthState";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SignInFormSchema = z.object({
  emailOrUsername: z.string().min(1, "ກະລຸນາໃສ່ອີເມວຫຼືຊື່ຜູ້ໃຊ້"),
  password: z.string().min(6, { message: "ລະຫັດຜ່ານຕ້ອງຢ່າງນ້ອຍ 6 ຕົວອັກສອນ" }),
  rememberMe: z.boolean().optional(),
});

type ISignInFormSchema = z.infer<typeof SignInFormSchema>;

export default function SignInForm() {
  const navigate = useNavigate({ from: "/" });
  const { isLoading } = useAuthState();

  const form = RHF.useForm({
    defaultValues: { emailOrUsername: "", password: "" },
    resolver: zodResolver(SignInFormSchema),
  });

  const handleSubmit = async (value: ISignInFormSchema) => {
    const identifier = value.emailOrUsername.trim();
    const onSuccess = () => {
      navigate({ to: "/app/dashboard" });
      toast.success("ເຂົ້າລະບົບສໍາເລັດ");
    };
    const onError = (error: {
      error: { message?: string; statusText?: string };
    }) => {
      toast.error(error.error.message || error.error.statusText);
    };

    if (emailRegex.test(identifier)) {
      await authClient.signIn.email(
        { email: identifier, password: value.password },
        { onSuccess, onError },
      );
      return;
    }

    await authClient.signIn.username(
      { username: identifier, password: value.password },
      { onSuccess, onError },
    );
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <FormRoot<ISignInFormSchema> methods={form} onSubmit={handleSubmit}>
      <FormInput
        name="emailOrUsername"
        label="ອີເມວ ຫຼື ຊື່ຜູ້ໃຊ້"
        requiredMark
        placeholder="name@example.com ຫຼື username"
      />
      <FormPassword
        name="password"
        label="ລະຫັດຜ່ານ"
        requiredMark
        placeholder="********"
      />

      <FormCheckbox name="rememberMe" label="ຈໍາຂ້ອຍໄວ້" />

      <Button
        type="submit"
        isLoading={form.formState.isSubmitting}
        className="w-full"
      >
        ເຂົ້າລະບົບ
      </Button>
    </FormRoot>
  );
}
